import { type Reducer, useCallback, useReducer } from 'react'
import { ZodError } from 'zod'
import { useDebounceCallback } from 'usehooks-ts'
import { ReplaceValues } from '@synapex/tools/typescript'

type State<T extends object, Ctx = undefined> = {
    ctx: Ctx
    value: string
    parsedValue: T | null
} & (
    | { sync: true }
    | {
          sync: false
          errorMessage: string | null
      }
)

type Action<T extends object> =
    | {
          //enter text input
          type: 'value'
          payload: string
      }
    | {
          //select dropdown
          type: 'parsedValue'
          payload: T | null
      }
    | {
          //set parsed value from string
          //debounce-update dropdown after entering text input
          type: 'sync'
      }
    | {
          //set string from parsed value(normalize it)
          //blur text input, Enter
          type: 'normalize'
      }
    | {
          //if need parsed state right now
          //return it and set to store
          type: 'set'
          payload: {} & (
              | { ok: true; normalizedValue: string; parsedValue: T | null }
              | {
                    ok: false
                    invalidValue: string
                    errorMessage: string
                }
          )
      }

// | {
//       type: 'sync_stringify'
//   }

export type ParsedInputFormat<T extends object, Ctx = undefined> = {
    parse: (ctx: Ctx, input: string) => T
    stringify: (ctx: Ctx, value: T) => string
}
export type ParsedInputReducer<T extends object, Ctx = undefined> = {
    stringify: (ctx: Ctx, value: T) => string
    syncParse: (
        ctx: Ctx,
        value: string
    ) =>
        | { ok: true; parsedValue: T | null }
        | { ok: false; errorMessage: string }
    reducer: Reducer<State<T, Ctx>, Action<T>>
    init: (arg: { parsedValue: T | null; ctx: Ctx }) => State<T, Ctx>
}
export const makeReducer = <T extends object, Ctx = undefined>({
    parse,
    stringify,
}: ParsedInputFormat<T, Ctx>): ParsedInputReducer<T, Ctx> => {
    const syncParse = (ctx: Ctx, value: string) => {
        try {
            const trimmed = value.trim()

            return {
                ok: true as const,
                parsedValue: trimmed ? parse(ctx, trimmed) : null,
            }
        } catch (error: unknown) {
            let errorMessage
            if (error instanceof Error) {
                if (error instanceof ZodError) {
                    errorMessage = error.issues[0].message
                } else {
                    errorMessage = error.message
                }
            } else {
                errorMessage = `Error unknown: ${typeof error}`
            }

            return {
                ok: false as const,
                errorMessage,
            }
        }
    }

    return {
        stringify,
        syncParse,
        init: ({ parsedValue, ctx }) => ({
            ctx,
            sync: true as const,
            parsedValue,
            value: parsedValue ? stringify(ctx, parsedValue) : '',
        }),
        reducer: (state, action) => {
            console.log({ state, action })
            switch (action.type) {
                case 'value': {
                    if (state.sync) {
                        return {
                            ...state,
                            value: action.payload,
                            sync: false,
                            errorMessage: null,
                        }
                    } else {
                        return {
                            ...state,
                            value: action.payload,
                        }
                    }
                }
                case 'parsedValue': {
                    //select UTM param in dropdown
                    return {
                        ...state,
                        parsedValue: action.payload,
                        value: action.payload
                            ? stringify(state.ctx, action.payload)
                            : '',
                        sync: true,
                    }
                }
                case 'sync': {
                    const result = syncParse(state.ctx, state.value)

                    if (result.ok) {
                        return {
                            ...state,
                            parsedValue: result.parsedValue,
                            sync: true,
                        }
                    } else {
                        return {
                            ...state,
                            sync: false,
                            errorMessage: result.errorMessage,
                        }
                    }
                }
                case 'normalize': {
                    if (state.sync) {
                        return {
                            ...state,
                            value: state.parsedValue
                                ? stringify(state.ctx, state.parsedValue)
                                : '',
                        }
                    }

                    return state
                }
                case 'set': {
                    const result = action.payload

                    if (result.ok) {
                        return {
                            ...state,
                            sync: true,
                            value: result.normalizedValue,
                            parsedValue: result.parsedValue,
                        }
                    } else {
                        return {
                            ...state,
                            sync: false,
                            value: result.invalidValue,
                            errorMessage: result.errorMessage,
                        }
                    }
                }
            }
        },
    }
}

//need stringify
export type UseParsedInputArgs<T extends object, Ctx = undefined> = {
    initialParsedValue: T | null
    ctx: Ctx
} & ParsedInputReducer<T, Ctx>

export const useParsedInput = <T extends object, Ctx = undefined>({
    initialParsedValue,
    ctx,
    //pure
    stringify,
    syncParse,
    reducer,
    init,
}: UseParsedInputArgs<T, Ctx>) => {
    const [state, dispatch] = useReducer(
        reducer,
        { parsedValue: initialParsedValue, ctx },
        init
    )

    const syncDebounced = useDebounceCallback(
        () => dispatch({ type: 'sync' }),
        250
    )

    const onNormalize = useCallback(() => dispatch({ type: 'normalize' }), [])

    const onChange = useCallback(
        (value: string) => {
            console.log({ value })
            dispatch({ type: 'value', payload: value })
            syncDebounced()
            console.log({ value })
        },
        [syncDebounced]
    )

    const onChangeParsedValue = useCallback((parsedValue: T | null) => {
        dispatch({ type: 'parsedValue', payload: parsedValue })
    }, [])

    const getSyncedValue = () => {
        syncDebounced.cancel()
        const result = syncParse(state.ctx, state.value)

        const payload = result.ok
            ? {
                  ok: true as const,
                  normalizedValue: result.parsedValue
                      ? stringify(state.ctx, result.parsedValue)
                      : '',
                  parsedValue: result.parsedValue,
              }
            : {
                  ok: false as const,
                  invalidValue: state.value,
                  errorMessage: result.errorMessage,
              }

        dispatch({
            type: 'set',
            payload,
        })

        return payload
    }

    return {
        value: state.value,
        onChange,
        // syncedValue: state.sync ? state.value : undefined,
        getSyncedValue,

        parsedValue: state.parsedValue,
        onChangeParsedValue,
        onNormalize,

        errorMessage: state.sync
            ? undefined
            : (state.errorMessage ?? undefined),
    }
}
export type UseParsedInput<T extends object> = ReplaceValues<
    ReturnType<typeof useParsedInput>,
    'parsedValue' | 'onChangeParsedValue',
    {
        parsedValue: T | null
        onChangeParsedValue: (parsedValue: T | null) => void
    }
>
